import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useGetChatSettingsQuery,
  useSetChatWallpaperMutation,
  useUpdateChatSettingsMutation,
} from "@/services/(my-space)/api.chat";
import { getErrorMessage } from "@/utils/error.utils";
import { cn } from "@/utils/cn";
import type {
  ChatWallpaper,
  IChatConversationDetail,
} from "@/types/(my-space)/chat.interface";
import { CHAT_WALLPAPERS, chatWallpaperSpec } from "../_lib/chat-wallpapers";

type Scope = "chat" | "all";
/** Scope values are data (tab ids), not labels. */
const SCOPES: Scope[] = ["chat", "all"];

/**
 * Pick a background for this chat, or the default for every chat. Applies on
 * tap; the room behind repaints from the refreshed detail.
 */
export const WallpaperDialog = ({
  open,
  detail,
  onClose,
}: {
  open: boolean;
  detail: IChatConversationDetail;
  onClose: () => void;
}) => {
  const { t } = useTranslation("chat");
  const [scope, setScope] = useState<Scope>("chat");
  const { data: settings } = useGetChatSettingsQuery(undefined, { skip: !open });
  const [setChatWallpaper, chatState] = useSetChatWallpaperMutation();
  const [updateSettings, allState] = useUpdateChatSettingsMutation();
  const busy = chatState.isLoading || allState.isLoading;
  const myDefault = settings?.wallpaper ?? "default";

  useEffect(() => {
    if (open) setScope("chat");
  }, [open]);

  const apply = async (wallpaper: ChatWallpaper | null) => {
    if (busy) return;
    try {
      if (scope === "chat") {
        await setChatWallpaper({ id: detail.id, wallpaper }).unwrap();
      } else if (wallpaper) {
        await updateSettings({ wallpaper }).unwrap();
      }
      toast.success(t("wallpaper.updated"));
      onClose();
    } catch (e) {
      toast.error(getErrorMessage(e, t("wallpaper.error")));
    }
  };

  const selected: ChatWallpaper | null = scope === "chat" ? detail.wallpaper : myDefault;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex max-h-[88vh] flex-col sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("wallpaper.title")}</DialogTitle>
          <DialogDescription>
            {scope === "chat" ? t("wallpaper.this_chat_hint") : t("wallpaper.all_chats_hint")}
          </DialogDescription>
        </DialogHeader>

        <div className="inline-flex self-start rounded-md bg-muted p-1" role="tablist">
          {SCOPES.map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={scope === value}
              onClick={() => setScope(value)}
              className={cn(
                "rounded-sm px-3 py-1.5 text-sm font-medium transition-colors",
                scope === value
                  ? "bg-background text-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {value === "chat" ? t("wallpaper.this_chat") : t("wallpaper.all_chats")}
            </button>
          ))}
        </div>

        <div className="grid min-h-0 grid-cols-3 gap-3 overflow-y-auto p-1 sm:grid-cols-4 lg:grid-cols-5">
          {scope === "chat" ? (
            <WallpaperTile
              wallpaper={myDefault}
              label={t("wallpaper.follow_default")}
              selected={selected === null}
              disabled={busy}
              onPick={() => void apply(null)}
            />
          ) : null}
          {CHAT_WALLPAPERS.map((w) => (
            <WallpaperTile
              key={w.key}
              wallpaper={w.key}
              label={t(`wallpaper.names.${w.key}`)}
              selected={selected === w.key}
              disabled={busy}
              onPick={() => void apply(w.key)}
            />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** A small preview: the background with an incoming and an outgoing bubble. */
const WallpaperTile = ({
  wallpaper,
  label,
  selected,
  disabled,
  onPick,
}: {
  wallpaper: ChatWallpaper;
  label: string;
  selected: boolean;
  disabled: boolean;
  onPick: () => void;
}) => {
  const spec = chatWallpaperSpec(wallpaper);
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={disabled}
      aria-pressed={selected}
      className="group flex flex-col gap-1.5 text-start disabled:opacity-70"
    >
      <span
        className={cn(
          "relative flex aspect-[3/4] w-full flex-col justify-end gap-1.5 overflow-hidden rounded-md border p-2 transition",
          !spec.style && "bg-muted/30",
          selected
            ? "border-primary ring-2 ring-primary"
            : "border-border group-hover:border-primary/60",
        )}
        style={spec.style}
      >
        <span className="h-2.5 w-3/5 rounded-full bg-surface shadow-xs" />
        <span className="h-2.5 w-1/2 self-end rounded-full bg-primary" />
        {selected ? (
          <span className="absolute end-1.5 top-1.5 flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Check className="size-3" />
          </span>
        ) : null}
      </span>
      <span
        className={cn(
          "truncate text-xs",
          selected ? "font-semibold text-foreground" : "text-muted-foreground",
        )}
      >
        {label}
      </span>
    </button>
  );
};
